import { useQuery } from '@tanstack/react-query';
import fetchAPI from '../fetchAPI';

export type MarkupHistoryRole = 'base' | 'response' | 'sentiment' | 'enquiries' | 'meta';
export type MarkupHistoryField =
  | 'muValue'
  | 'price'
  | 'inputPrice'
  | 'outputPrice'
  | 'model'
  | 'title'
  | 'description'
  // SR-H6: per-enquiry-type overrides on the response purpose only.
  | 'muValueSales'
  | 'muValueLettings'
  | 'modelSales'
  | 'modelLettings';

export interface MarkupHistoryRow {
  id: string;
  markupMarginsId: string;
  type: string;
  role: MarkupHistoryRole;
  field: MarkupHistoryField;
  oldValue: string | null;
  newValue: string;
  changedAt: string;
  changedBy: string | null;
  changedByUserName: string | null;
}

interface Props {
  type?: string;
  // Gate the fetch until the modal actually opens.
  enabled?: boolean;
}

export const useMarkupHistory = ({ type, enabled = true }: Props) => {
  return useQuery({
    queryKey: ['markup/history', type],
    // Unwrap the fetchAPI envelope so callers get MarkupHistoryRow[] directly.
    queryFn: async (): Promise<MarkupHistoryRow[]> => {
      const response = await fetchAPI<{ data: MarkupHistoryRow[] }>({
        method: 'GET',
        route: `markup/${type}/history`,
      });
      return response?.data?.data ?? [];
    },
    enabled: Boolean(type) && enabled,
    // Same reopen-for-fresh pattern as usePriceModelHistory: the user
    // typically opens this modal right after editing a markup value to
    // verify the new row appears.
    staleTime: 0,
    refetchOnMount: 'always',
  });
};

export default useMarkupHistory;
